/** The boundary editor's state and every change to it, pure (boundary-editor design §4.3). */
import type { LngLat } from '@/planner/types';
import { deleteVertex, insertVertex, moveVertex } from '@/features/missions/polygonEditor';
import type { DrawnPolygon, Ring } from './geometry';

export type Mode = 'view' | 'draw' | 'hole' | 'edit';
/** A ring of a polygon; `hole: -1` is the shell. */
export interface RingRef {
  polygon: number;
  hole: number;
}
/** What undo restores: the polygons and the boundary they were started from. */
export interface Snapshot {
  polygons: DrawnPolygon[];
  source: string | null;
}
export interface EditorState {
  polygons: DrawnPolygon[];
  /** The project boundary the polygons were started from, or null. */
  source: string | null;
  selected: number | null;
  mode: Mode;
  draft: Ring;
  history: Snapshot[];
  /** Counts replaces; the map fits to the polygons whenever it changes. */
  replaced: number;
}
export type Action =
  | { type: 'mode'; mode: Mode }
  | { type: 'select'; polygon: number | null }
  | { type: 'click'; at: LngLat }
  /** `dropLast`: a double-click's second click appended a duplicate of the last vertex. */
  | { type: 'close'; dropLast?: boolean }
  | { type: 'cancel' }
  | { type: 'dragStart' }
  | { type: 'move'; ring: RingRef; index: number; at: LngLat }
  | { type: 'insert'; ring: RingRef; after: number; at: LngLat }
  | { type: 'deleteVertex'; ring: RingRef; index: number }
  | { type: 'deleteRing'; ring: RingRef }
  /** `source`: the project boundary the polygons come from; an import has none. */
  | { type: 'replace'; polygons: DrawnPolygon[]; source?: string | null }
  | { type: 'undo' };

const HISTORY_MAX = 100;

export function initialState(polygons: DrawnPolygon[] = []): EditorState {
  return { polygons, source: null, selected: polygons.length ? 0 : null, mode: polygons.length ? 'view' : 'draw', draft: [], history: [], replaced: 0 };
}

export function ringAt(ps: DrawnPolygon[], ref: RingRef): Ring {
  const p = ps[ref.polygon];
  return ref.hole < 0 ? p.shell : p.holes[ref.hole];
}

function withRing(ps: DrawnPolygon[], ref: RingRef, ring: Ring): DrawnPolygon[] {
  return ps.map((p, i) => {
    if (i !== ref.polygon) return p;
    return ref.hole < 0 ? { ...p, shell: ring } : { ...p, holes: p.holes.map((h, j) => (j === ref.hole ? ring : h)) };
  });
}

/** A change that can be undone: the current polygons go onto the history first. */
function commit(s: EditorState, polygons: DrawnPolygon[], rest: Partial<EditorState> = {}): EditorState {
  return { ...s, ...rest, polygons, history: [...s.history, { polygons: s.polygons, source: s.source }].slice(-HISTORY_MAX) };
}

export function reducer(s: EditorState, a: Action): EditorState {
  switch (a.type) {
    case 'mode':
      if (a.mode === 'hole' && s.selected === null) return { ...s, mode: 'view', draft: [] };
      return { ...s, mode: a.mode, draft: [] };
    case 'select':
      return { ...s, selected: a.polygon !== null && a.polygon < s.polygons.length ? a.polygon : null };
    case 'click':
      return s.mode === 'draw' || s.mode === 'hole' ? { ...s, draft: [...s.draft, a.at] } : s;
    case 'close': {
      const draft = a.dropLast ? s.draft.slice(0, -1) : s.draft;
      if (draft.length < 3) return s;
      if (s.mode === 'draw') return commit(s, [...s.polygons, { shell: draft, holes: [] }], { selected: s.polygons.length, mode: 'edit', draft: [] });
      if (s.mode === 'hole' && s.selected !== null) {
        const sel = s.selected;
        return commit(
          s,
          s.polygons.map((p, i) => (i === sel ? { ...p, holes: [...p.holes, draft] } : p)),
          { mode: 'edit', draft: [] },
        );
      }
      return s;
    }
    case 'cancel':
      return { ...s, draft: [] };
    case 'dragStart':
      return commit(s, s.polygons);
    case 'move':
      return { ...s, polygons: withRing(s.polygons, a.ring, moveVertex(ringAt(s.polygons, a.ring), a.index, a.at)) };
    case 'insert':
      return commit(s, withRing(s.polygons, a.ring, insertVertex(ringAt(s.polygons, a.ring), a.after, a.at)));
    case 'deleteVertex': {
      const ring = ringAt(s.polygons, a.ring);
      const next = deleteVertex(ring, a.index);
      return next === ring ? s : commit(s, withRing(s.polygons, a.ring, next));
    }
    case 'deleteRing': {
      if (a.ring.hole >= 0) {
        return commit(
          s,
          s.polygons.map((p, i) => (i === a.ring.polygon ? { ...p, holes: p.holes.filter((_, j) => j !== a.ring.hole) } : p)),
        );
      }
      const polygons = s.polygons.filter((_, i) => i !== a.ring.polygon);
      const sel = s.selected;
      const selected = sel === null || sel === a.ring.polygon ? null : sel > a.ring.polygon ? sel - 1 : sel;
      return commit(s, polygons, { selected });
    }
    case 'replace':
      return commit(s, a.polygons, {
        source: a.source ?? null,
        selected: a.polygons.length ? 0 : null,
        mode: a.polygons.length ? 'view' : 'draw',
        draft: [],
        replaced: s.replaced + 1,
      });
    case 'undo': {
      if (!s.history.length) return s;
      const { polygons, source } = s.history[s.history.length - 1];
      return { ...s, polygons, source, history: s.history.slice(0, -1), selected: s.selected !== null && s.selected < polygons.length ? s.selected : null, draft: [] };
    }
  }
}
